import {
  Component,
  PLATFORM_ID,
  computed,
  inject,
  input,
  linkedSignal,
  signal,
  viewChild,
  OnInit,
} from '@angular/core';
import { NodeSercon, Teaser } from '@app/models/entity.model';
import { ServiceInfoboxComponent } from '@app/components/service-infobox/service-infobox.component';
import { ParagraphLoaderComponent } from '@app/entities/paragraph/loader.component';

import { Icon } from '@app/models/global.model';
import { ParagraphPgMscs, ParagraphPgPnav } from '@app/models/paragraph.model';
import { TextsPipe } from '@app/pipes/texts.pipe';
import {
  MobiUiSplitButtonComponent,
  MobiUiSplitButtonGroupComponent,
} from '@mobi/rwc-ui-components-ng-jslib';
import { HeroTextComponent } from '@app/components/hero-text/hero-text.component';
import { BreadcrumbComponent } from '@app/components/breadcrumb/breadcrumb.component';
import { ContentSectionComponent } from '@app/components/content-section/content-section.component';
import { ConciergeComponent } from '@app/components/concierge/concierge.component';
import { ContentLinksComponent } from '@app/components/content-links/content-links.component';
import { TextsService } from '@app/services/texts.service';
import { TeaserListComponent } from '@app/components/teaser-list/teaser-list.component';
import { SectionTitleComponent } from '@app/components/section-title/section-title.component';
import { IllustrationComponent } from '@app/components/illustration/illustration.component';
import { GaSearchComponent } from '@app/components/ga-search/ga-search.component';
import { EntitySharedModule } from '@app/modules/entity.shared.module';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { setTimeout$ } from '@app/helpers/timeout';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'mobi-node-sercon',
  templateUrl: './node-sercon.component.html',
  styleUrls: ['./node-sercon.component.scss'],
  standalone: true,
  imports: [
    EntitySharedModule,
    ServiceInfoboxComponent,
    TextsPipe,
    MobiUiSplitButtonGroupComponent,
    MobiUiSplitButtonComponent,
    HeroTextComponent,
    BreadcrumbComponent,
    ContentSectionComponent,
    ConciergeComponent,
    ContentLinksComponent,
    TeaserListComponent,
    SectionTitleComponent,
    GaSearchComponent,
    IllustrationComponent,
    ParagraphLoaderComponent,
  ],
})
export class NodeSerconComponent implements OnInit {
  readonly entity = input.required<NodeSercon>();
  readonly params = input<URLSearchParams>(new URLSearchParams());
  visiblePage = linkedSignal<string>(() =>
    this.params().get('page') === 'contact' ? 'contact' : 'services'
  );
  hasSwitchedPage = signal(false);
  contentLinks = signal<ParagraphPgPnav[]>([]);

  readonly splitButtonGroup =
    viewChild<MobiUiSplitButtonGroupComponent>('splitButtonGroup');

  emergencyCtaIcon: Icon = {
    type: 'TaxonomyTermIcons',
    label: 'message_sos',
    symbol: 'message_sos',
  };
  #texts = inject(TextsService);
  #document = inject(DOCUMENT);
  #platformId = inject(PLATFORM_ID);

  async ngOnInit() {
    if (this.params().get('page') === 'services') {
      this.updatePageParam('services');
    }

    await this.getContentLinks();
  }

  mainTeasers = computed<Teaser[]>(() => {
    const mainServices = this.entity()?.mainServices;

    if (!mainServices || mainServices.length === 0) {
      return [];
    }

    return mainServices.map((teaser: ParagraphPgMscs) => ({
      teaserTitle: teaser.title,
      teaserTextShort: teaser.cta.title,
      url: teaser.cta,
      teaserIllustration: teaser?.illustration,
      created: '',
      bundle: 'ParagraphPgMscs',
      published: true,
      uuid: teaser.uuid,
    }));
  });

  async getContentLinks() {
    /*
      check for all structured content if it is
      available and show the content links for the resp. paragraphs
       */
    if (this.entity().damageReportServicesTitle) {
      const label: string = await this.#texts.getText(
        'damage_report_services_name',
        'frontend',
        'Schaden'
      );
      const sectionId: string = await this.#texts.getText(
        'damage_report_services_paramenter_name',
        'frontend',
        'schaden'
      );
      const link: ParagraphPgPnav = {
        label,
        sectionId: sectionId,
      };
      this.contentLinks.update((values) => [...values, link]);
    }
    if (this.entity().administrativeServicesTitle) {
      await Promise.all([
        this.#texts.getText(
          'administrative_services_name',
          'frontend',
          'Administratives'
        ),
        this.#texts.getText(
          'administrative_services_parameter_name',
          'frontend',
          'administratives'
        ),
      ])
        .then((texts) => {
          const link: ParagraphPgPnav = {
            label: texts[0],
            sectionId: texts[1],
          };
          this.contentLinks.update((values) => [...values, link]);
        })
        .catch((e) => this.#handleError(e));
    }
    if (this.entity().mobilityServicesTitle) {
      await Promise.all([
        this.#texts.getText('mobility_services_name', 'frontend', 'Mobilität'),
        this.#texts.getText(
          'mobility_services_paramenter_name',
          'frontend',
          'mobilitaet'
        ),
      ])
        .then((texts) => {
          const link: ParagraphPgPnav = {
            label: texts[0],
            sectionId: texts[1],
          };
          this.contentLinks.update((values) => [...values, link]);
        })
        .catch((e) => this.#handleError(e));
    }
    if (this.entity().invoiceServicesTitle) {
      await Promise.all([
        this.#texts.getText('invoice_services_name', 'frontend', 'Rechnungen'),
        this.#texts.getText('invoice_services_paramenter_name', 'frontend', 'rechnungen'),
      ])
        .then((texts) => {
          const link: ParagraphPgPnav = {
            label: texts[0],
            sectionId: texts[1],
          };
          this.contentLinks.update((values) => [...values, link]);
        })
        .catch((e) => this.#handleError(e));
    }
    if (this.entity().customerBenefitsServicesTitle) {
      await Promise.all([
        this.#texts.getText(
          'customer_benefits_services_name',
          'frontend',
          'Kundenvorteile'
        ),
        this.#texts.getText(
          'customer_benefits_services_paramenter_name',
          'frontend',
          'kundenvorteile'
        ),
      ])
        .then((texts) => {
          const link: ParagraphPgPnav = {
            label: texts[0],
            sectionId: texts[1],
          };
          this.contentLinks.update((values) => [...values, link]);
        })
        .catch((e) => this.#handleError(e));
    }
  }

  changePage(page: string) {
    this.hasSwitchedPage.set(true);
    this.visiblePage.set(page);
    this.updatePageParam(page);
    setTimeout$(() => {
      this.#document.getElementById('scroll-to-view')?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }, 100);
  }

  #handleError(e: any) {
    console.error(e);
    this.#document?.dispatchEvent(
      new CustomEvent('tracking:system', {
        detail: {
          message:
            e.error?.message ||
            e.message ||
            `An unknown error occurred: ${JSON.stringify(e)}`,
          type: 'error',
        },
      })
    );
  }

  updatePageParam(page: string) {
    if (!isPlatformBrowser(this.#platformId)) return;

    const url = new URL(this.#document.location.href);
    if (page === 'contact') {
      url.searchParams.set('page', page);
    } else {
      url.searchParams.delete('page');
    }
    window.history.replaceState(window.history.state, '', url.toString());
  }
}
